import { apiFetch } from '@/lib/api';
import type { Member } from '@domain/types';

export type MemberInput = {
  name: string;
  phone?: string | null;
  avatar?: string | null;
};

export type MemberUsage = {
  paidExpenses: number;
  participations: number;
  settlements: number;
};

export function usageTotal(usage: MemberUsage): number {
  return usage.paidExpenses + usage.participations + usage.settlements;
}

export class MemberInUseError extends Error {
  constructor(public usage: MemberUsage) {
    super('This member is referenced by existing records.');
    this.name = 'MemberInUseError';
  }
}

export async function listMembers(groupId: string, includeInactive = true): Promise<Member[]> {
  return apiFetch<Member[]>(`/groups/${groupId}/members?includeInactive=${includeInactive}`);
}

export async function createMember(groupId: string, input: MemberInput): Promise<Member> {
  return apiFetch<Member>(`/groups/${groupId}/members`, {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

export async function updateMember(
  id: string,
  patch: Partial<MemberInput & { isActive: boolean }>
): Promise<void> {
  await apiFetch<void>(`/members/${id}`, {
    method: 'PUT',
    body: JSON.stringify(patch),
  });
}

export async function getMemberUsage(id: string): Promise<MemberUsage> {
  const usage = await apiFetch<MemberUsage>(`/members/${id}/usage`);
  return {
    paidExpenses: usage.paidExpenses,
    participations: usage.participations,
    settlements: usage.settlements,
  };
}

export async function deleteMember(id: string): Promise<void> {
  try {
    await apiFetch<void>(`/members/${id}`, {
      method: 'DELETE',
    });
  } catch (error) {
    const usage = await getMemberUsage(id);
    if (usageTotal(usage) > 0) {
      throw new MemberInUseError(usage);
    }
    throw error;
  }
}
